import { AxiosError } from "axios";
export enum ApiErrorType {
    NETWORK = "NETWORK",
    TIMEOUT = "TIMEOUT",
    NOT_FOUND = "NOT_FOUND",
    VALIDATION = "VALIDATION",
    UNAUTHORIZED = "UNAUTHORIZED",
    FORBIDDEN = "FORBIDDEN",
    CONFLICT = "CONFLICT",
    RATE_LIMIT = "RATE_LIMIT",
    SERVER_ERROR = "SERVER_ERROR",
    UNKNOWN = "UNKNOWN",
}
export class ApiError extends Error {
    constructor(
        public type: ApiErrorType,
        public statusCode?: number,
        public originalError?: unknown,
        message?: string
    ) {
        super(message || `API Error: ${type}`);
        this.name = "ApiError";
    }
}
export const classifyError = (status: number): ApiErrorType => {
    if (status === 400 || status === 422) return ApiErrorType.VALIDATION;
    if (status === 401) return ApiErrorType.UNAUTHORIZED;
    if (status === 403) return ApiErrorType.FORBIDDEN;
    if (status === 404) return ApiErrorType.NOT_FOUND;
    if (status === 409) return ApiErrorType.CONFLICT;
    if (status === 429) return ApiErrorType.RATE_LIMIT;
    if (status >= 500) return ApiErrorType.SERVER_ERROR;
    return ApiErrorType.UNKNOWN;
};

export const getErrorMessage = (error: AxiosError<any>): string => {
    return (
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Unknown error occurred"
    );
};

export const isApiError = (error: unknown): error is ApiError => {
    return error instanceof ApiError;
};
export const isErrorType = (error: unknown, type: ApiErrorType): boolean => {
    return isApiError(error) && error.type === type;
};

const getTranslation = (key: string): string => {
    const fallbacks: Record<string, string> = {
        "api_errors.network": "Check your internet connection",
        "api_errors.timeout": "Request took too long - please try again",
        "api_errors.unauthorized": "Your session expired - please login again",
        "api_errors.forbidden": "You don't have permission to access this resource",
        "api_errors.not_found": "Resource not found",
        "api_errors.validation": "Please check your input",
        "api_errors.conflict": "This item already exists or has been modified",
        "api_errors.rate_limit": "Too many requests - please wait a moment",
        "api_errors.server_error": "Server error - please try again later",
        "api_errors.unknown": "Something went wrong",
    };
    return fallbacks[key] || key;
};

export const getUserErrorMessage = (error: unknown): string => {
    if (!isApiError(error)) return getTranslation("api_errors.unknown");

    const errorTypeMap: Record<ApiErrorType, string> = {
        [ApiErrorType.NETWORK]: getTranslation("api_errors.network"),
        [ApiErrorType.TIMEOUT]: getTranslation("api_errors.timeout"),
        [ApiErrorType.UNAUTHORIZED]: getTranslation("api_errors.unauthorized"),
        [ApiErrorType.FORBIDDEN]: getTranslation("api_errors.forbidden"),
        [ApiErrorType.NOT_FOUND]: getTranslation("api_errors.not_found"),
        [ApiErrorType.VALIDATION]: error.message || getTranslation("api_errors.validation"),
        [ApiErrorType.CONFLICT]: getTranslation("api_errors.conflict"),
        [ApiErrorType.RATE_LIMIT]: getTranslation("api_errors.rate_limit"),
        [ApiErrorType.SERVER_ERROR]: getTranslation("api_errors.server_error"),
        [ApiErrorType.UNKNOWN]: error.message || getTranslation("api_errors.unknown"),
    };

    return errorTypeMap[error.type] || getTranslation("api_errors.unknown");
};
